/**
 * Les écritures du catalogue sur une DÉFINITION de bloc (`builder_def`).
 *
 * Trois appels, deux règles qui ne sont PAS dans ce fichier :
 *
 *  - les métadonnées (`nom`, `catégorie`, `description`) ne sont pas toutes des
 *    champs Drupal : deux d'entre elles vivent dans le YAML verbatim de la
 *    définition, à côté du `rjBuild`. C'est Drupal qui les y écrit sans
 *    reformater le document (BlockDefMetaPatcher) — d'où un endpoint plutôt
 *    qu'un `/decoupled-api/save` ;
 *  - une définition encore posée quelque part ne se supprime pas. Le refus est
 *    tranché côté serveur, sur un compte AVEUGLE aux droits : l'écran ne fait
 *    que raconter le verdict (409), il ne le décide pas.
 *
 * `credentials` et le jeton CSRF sont ajoutés par le patch global de `fetch`
 * (cf. services/fetchPatch.js) : `/api/…` en fait partie.
 */

const META_URL = '/api/builder/def/meta'
const DELETE_URL = '/api/builder/def/delete'

/** Le message de Drupal, ou un repli lisible. */
const messageOf = async (res, fallback) => {
  try {
    const body = await res.json()
    return body?.message || fallback
  } catch {
    return fallback
  }
}

/**
 * Écrit les métadonnées d'une définition. PATCH : seules les clés passées sont
 * modifiées — ne pas passer `siteId` laisse le rattachement tel quel, le passer
 * à `null` détache.
 *
 * @returns {Promise<{nid:number, name:string, category:string, description:string,
 *   definition:string, siteId:number|null, siteName:string}>}
 * @throws {Error} message affichable tel quel dans la pop-in.
 */
export async function saveDefMeta(patch) {
  const res = await fetch(META_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  if (!res.ok) {
    throw new Error(await messageOf(res, `Échec de l'enregistrement (${res.status}).`))
  }
  return res.json()
}

/**
 * Supprime une définition.
 *
 * Le 409 n'est pas une erreur de transport mais une RÉPONSE : « ce bloc est
 * utilisé, voilà par quoi ». D'où un retour à deux formes plutôt qu'une
 * exception — la pop-in bascule sur la liste des contenus au lieu d'afficher un
 * message rouge sans suite.
 *
 * @returns {Promise<{ok:true} | {ok:false, usage:object|null, message:string}>}
 * @throws {Error} pour tout autre échec (403, réseau…).
 */
export async function deleteDef(nid) {
  const res = await fetch(DELETE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nid: Number(nid) }),
  })
  if (res.status === 409) {
    const body = await res.json().catch(() => ({}))
    return { ok: false, usage: body?.usage ?? null, message: body?.message ?? 'Ce bloc est utilisé.' }
  }
  if (!res.ok) {
    throw new Error(await messageOf(res, `Échec de la suppression (${res.status}).`))
  }
  return { ok: true }
}

/**
 * Les sites visibles par l'utilisateur, `[{id, name}]` triés par nom.
 *
 * Via l'API générique decoupled : la liste est donc déjà filtrée par les droits
 * de Drupal — on ne peut rattacher un bloc qu'à un site qu'on a le droit de
 * voir, sans qu'aucune règle d'accès vive dans le front.
 */
export async function fetchSites(signal) {
  const res = await fetch('/decoupled-api/group/site/collection?display=decoupled', { signal })
  if (!res.ok) throw new Error(String(res.status))
  const payload = await res.json()
  // Enveloppe Syncable pour qui peut écrire le site, objet plat sinon : les deux
  // formes cohabitent selon le lecteur (cf. Navigation.yaml).
  const items = (Array.isArray(payload) ? payload : [])
    .map(item => item?.data ?? item)
    .filter(d => d && d.id != null)
    .map(d => ({ id: Number(d.id), name: d.name || `Site ${d.id}` }))
  items.sort((a, b) => a.name.localeCompare(b.name, 'fr'))
  return items
}
